/**
 * Ryan O'Dowd
 * 2018-05-28
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Image,
  Linking, Platform,
  View,
} from 'react-native';
import React, {
  useState,
} from 'react';
import AlertMessages from './AlertMessages';
import AppleAuthButton from './AppleAuthButton';
import AuthButton from './AuthButton';
import Button from '@components/common/Button';
import CustomAuthForm from './CustomAuthForm';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import GoogleAuthButton from './GoogleAuthButton';
import Header from '@components/Header';
import LocaleMenuButton from './LocaleMenuButton';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import fb from '@utils/firebase';
import getStyles from './styles';
import hooks from '@hooks';
// @TODO: when linking, no photoURL, and no update to name

const Auth = (props: AuthProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n(props.localeId);

  const [_welcomeVisible, setWelcomeVisible] = useState(!props.isLinking);
  const [_errorMessage, setErrorMessage] = useState('');

  const [_visibleCustomAuthForm, setVisibleCustomAuthForm] = useState(null);

  const [_authIsPending, setAuthIsPending] = useState(false);

  const authButtons = [];
  if (props.authTypes.includes('apple')) {
    authButtons.push(
      <AppleAuthButton
        key='appleButton'
        setErrorMessage={setErrorMessage}
        localeId={props.localeId}
        authIsPending={_authIsPending}
        setAuthIsPending={setAuthIsPending}
        isLinking={props.isLinking}// @TODO: only on mobile
      />,
    );
  }
  if (props.authTypes.includes('google')) {
    authButtons.push(
      <GoogleAuthButton
        key='googleButton'
        setErrorMessage={setErrorMessage}
        localeId={props.localeId}
        authIsPending={_authIsPending}
        setAuthIsPending={setAuthIsPending}
        isLinking={props.isLinking}// @TODO: only on mobile
      />,
    );
  }
  if (props.authTypes.includes('email')) { // @TODO: email is passwordless, so it won't work on desktop...need to work around this because existing users can't log in on desktop if they've used the email provider
    authButtons.push(
      <AuthButton
        key='email'
        iconName='email'
        providerName='Email'
        localeId={props.localeId}
        authIsPending={_authIsPending}
        setAuthIsPending={setAuthIsPending}
        isLinking={props.isLinking}// @TODO: only on mobile
        onPress={() => setVisibleCustomAuthForm('email')}
      />,
    );
  }
  if (['android', 'web'].includes(Platform.OS)) {
    // apple should be the first sign-in option only on iOS...make it last on Android (except for anonymous) and web
    authButtons.push(authButtons.shift());
  }
  if (props.authTypes.includes('anonymous') && !props.isLinking) {
    authButtons.push(
      <Button
        key='anonymous'
        mode='text'
        style={styles.submitButton}
        disabled={_authIsPending}
        theme={styles.colors.white}
        onPress={() => setVisibleCustomAuthForm('anonymous')}
      >
        {i18n('continueAsGuest')}
      </Button>,
    );
  }

  const rightItems = props.i18nSupport ? [<LocaleMenuButton key='locale' setLocaleId={(localeId) => props.setLocaleId(localeId)} localeId={props.localeId} />] : [];

  const backIcon = (
    <Header.BackIcon
      onPress={() => {
        setAuthIsPending(false);
        setVisibleCustomAuthForm(null);
      }}
    />
  );

  return (
    <ScreenWrapper noWebHeader={true}>
      {!props.noHeader && !props.headerComponent &&
        (_visibleCustomAuthForm
          ? (
            <Header
              textColor={styles.colors.white}
              leftItems={[backIcon]}
              title=''
            />
          ) : (
            <Header
              backgroundColor='#00000000'
              textColor={styles.colors.white}
              rightItems={rightItems}
              title=''
            />
          )
        )
      }
      {props.headerComponent}
      <FullScreenScrollView contentContainerStyle={styles.container}>
        <View style={styles.innerContainer}>
          {!_welcomeVisible && !!_visibleCustomAuthForm &&
            <CustomAuthForm
              authType={_visibleCustomAuthForm}
              localeId={props.localeId}
              isLinking={props.isLinking}
            />
          }
          {!_visibleCustomAuthForm &&
            <React.Fragment>
              <View style={styles.loginContainer}>
                <View style={styles.brandingContainer}>
                  <View style={styles.logoBackground}>
                    <Image
                      style={styles.logo}
                      source={require('@assets/img/logo.png')}// @TODO: update this for wrinkly (and move to ./assets/)
                    />
                  </View>
                  <Text style={styles.appName}>Wrinkly</Text>
                  {props.linkInstructions && <Text style={styles.linkInstructions}>{props.linkInstructions}</Text>}
                </View>
              </View>
              <AlertMessages errorMessage={_errorMessage} />
              <View style={styles.lowerContentWrapper}>
                {(!props.isLinking && _welcomeVisible)
                  ? (
                    <View style={styles.welcomeContainer}>
                      <Text style={styles.welcomeHeaderText}>{''}</Text>{/* @TODO: need this? */}
                      <View style={styles.verse}>
                        <Text style={styles.verseText}>{i18n('psalmText')}</Text>
                        <Text style={styles.verseText}>{i18n('psalmReference')}</Text>
                      </View>
                      <Button
                        style={styles.authPaperButton}
                        mode='contained'
                        theme={styles.colors.white}
                        onPress={() => {
                          if (props.noSignUp) {
                            fb.signInAnonymously(fb.auth); // @TODO: only allow on mobile
                          } else {
                            setWelcomeVisible(false);
                          }
                        }}
                      >
                        {i18n('getStarted')}
                      </Button>
                    </View>
                  ) : (
                    <View style={styles.buttonsWrapper}>
                      {authButtons}
                    </View>
                  )
                }
              </View>
              {!_welcomeVisible &&
                <View style={styles.disclaimerWrapper}>
                  <Text style={styles.disclaimerText}>
                    {i18n('weRespect')}
                  </Text>
                  <Text
                    style={[styles.disclaimerText, styles.disclaimerLink]}
                    onPress={() => Linking.openURL('https://oakwoodsc.com/#terms_and_privacy')}
                  >
                    {i18n('privacyAndTerms')}
                  </Text>
                </View>
              }
            </React.Fragment>
          }
        </View>
      </FullScreenScrollView>
    </ScreenWrapper>
  );
};

class AuthProps {
  authTypes: ('anonymous' | 'google' | 'apple' | 'email')[]; // @TODO: anonymous is only allowed if not web (ternary must return value not type)
  localeId: any;
  setLocaleId: any;

  i18nSupport?: boolean;
  isLinking?: boolean;
  linkInstructions?: string;
  noHeader?: boolean;
  noSignUp?: boolean;
  welcomeTextSpanish?: any;
  headerComponent?: React.ReactNode;
};

export default Auth;
